import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import emailjs from 'emailjs-com';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedService?: string;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, selectedService = 'general' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    service: selectedService
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init("TRfPX_qNPiv3z_DMS"); // Your EmailJS User ID
  }, []);

  // Update service when selectedService prop changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      service: selectedService
    }));
  }, [selectedService]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company,
        service_interest: formData.service,
        message: formData.message,
        to_name: 'Utkrsh Sharma'
      };

      const response = await emailjs.send(
        'service_vqwophk', // Your EmailJS Service ID
        'template_a13hch9', // Your EmailJS Template ID
        templateParams
      );

      if (response.status === 200) {
        setSubmitStatus('success');
        setStatusMessage('Thank you! Your message has been sent successfully. I\'ll get back to you within 24 hours.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
          service: selectedService
        });
        
        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 3000);
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Email send error:', error);
      setSubmitStatus('error');
      setStatusMessage('Sorry, there was an error sending your message. Please try again or contact me directly via email.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="modal-responsive"
          onClick={handleOverlayClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="modal-content-responsive"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Let's Discuss Your Project</h2>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Schedule a call to discuss your digital marketing needs</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full touch-friendly ml-4 flex-shrink-0"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Contact Form */}
            <div className="p-4 sm:p-6">
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm sm:text-base text-green-800">{statusMessage}</p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm sm:text-base text-red-800">{statusMessage}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="input-responsive"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="input-responsive"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="input-responsive"
                  />
                </div>
                
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                    Service Interest
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="select-responsive"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="technical">Technical SEO</option>
                    <option value="onpage">On-Page SEO</option>
                    <option value="social">Social Media Marketing</option>
                    <option value="gmb">Google My Business</option>
                    <option value="analytics">Analytics & Reporting</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows={4}
                    placeholder="Tell me about your project and goals..."
                    className="textarea-responsive"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-primary to-primary-dark text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-friendly"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span className="text-sm sm:text-base">Sending Message...</span>
                    </div>
                  ) : (
                    <span className="text-sm sm:text-base">Send Message</span>
                  )}
                </button>
              </form>

              {/* Contact Information */}
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Contact Information</h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3">
                    <FaPhone className="text-primary w-4 h-4 flex-shrink-0" />
                    <a 
                      href="tel:+919897785402"
                      className="text-sm sm:text-base text-gray-700 hover:text-primary transition-colors"
                    >
                      +91-9897785402
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaEnvelope className="text-primary w-4 h-4 flex-shrink-0" />
                    <a 
                      href="mailto:maheshsharma4651@gmail.com"
                      className="text-sm sm:text-base text-gray-700 hover:text-primary transition-colors break-all"
                    >
                      maheshsharma4651@gmail.com
                    </a>
                  </div>
                  <div className="flex items-start gap-3">
                    <FaMapMarkerAlt className="text-primary w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div className="text-sm sm:text-base text-gray-700">
                      MA-2, Pratap Apartment<br />
                      Jaipur, Rajasthan 302001<br />
                      India
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal; 